import { z } from 'zod';

import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

import operatorApiFetch from './apiService';

// ============================================================
// Schemas — 對齊揀補儲位 (pick-face replenishment) Plan 2 後端：
//   app/controllers/api/operator/v1/product_replenishment_shelves_controller.rb
//   app/controllers/api/operator/v1/replenishment_needs_controller.rb
//   app/controllers/api/operator/v1/replenishment_orders_controller.rb
// 與其下游 service：
//   app/services/replenishment/need_calculator.rb
//   app/services/replenishment/order_generator.rb
// 欄位真相以 controller 的 serialize_* block / NeedCalculator 回傳 hash 為準，
// nullability 以 db/structure.sql + serializer 是否用安全導航 (`&.`) 決定。
// ============================================================

// `serialize_slot` (product_replenishment_shelves_controller.rb:84-98)
//   index / get_records / show / create / update 都回這個 shape。
//   - product_name: serializer 用 `slot.product.name`（product 非 optional，name 欄位 NOT NULL）
//     仍標 nullable 以對齊 need/preview 的寬鬆契約（FE 統一以 nullable 處理產品名）。
//   - product_sku: products.sku 欄位 nullable。
//   - inventory_type_*: belongs_to :inventory_type optional → nullable。
//   - shelf_location: serializer 用 `slot.shelf.location`（shelf 非 optional，location NOT NULL）。
//   - capacity / replenishment_threshold: DB 欄位 nullable，model 用 allow_nil 驗證、無 presence 驗證。
//     讀取端 (index/get_records/show) 走 `.configured` scope 保證有值，但 create/update 回應
//     **不**走 `.configured`——後端可合法回傳 capacity: nil，故標 nullable 避免整包回應 ZodError。
export const ReplenishmentSlotSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  shelf_id: z.string(),
  shelf_location: z.string().nullable(),
  capacity: z.number().nullable(),
  replenishment_threshold: z.number().nullable(),
  merchant_id: z.string(),
}).passthrough();

// `enrich` 後的 NeedCalculator row (replenishment_needs_controller.rb:32-47
//   merge 進 need_calculator.rb:44-49 回傳的 hash)。
//   - product_name / product_sku / inventory_type_name / shelf_location / merchant_id
//     全用 `product&.` / `types[...]&.` / `shelves[...]&.` 安全導航 → nullable。
//   - 數值欄位 (on_face, min, capacity, need_minmax, wave_demand, need_demand, need)
//     由 NeedCalculator 計算後一律有值。
//   - over_capacity 為 boolean。
export const ReplenishmentNeedSchema = z.object({
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  shelf_id: z.string(),
  shelf_location: z.string().nullable(),
  on_face: z.number(),
  min: z.number(),
  capacity: z.number(),
  need_minmax: z.number(),
  wave_demand: z.number(),
  need_demand: z.number(),
  need: z.number(),
  over_capacity: z.boolean(),
  merchant_id: z.string().nullable(),
}).passthrough();

// preview item fan-out (replenishment_orders_controller.rb:105-113)
//   - batch: `inv&.batch`（inventories.batch nullable）→ nullable。
//   - expiration_date: `inv&.expiration_date`（date 欄位）→ nullable。
//   - from_shelf_location / to_shelf_location: `shelves[...]&.location` 安全導航 → nullable。
//   - suggested_quantity: alloc[:quantity]，必有值。
export const PreviewItemSchema = z.object({
  inventory_id: z.string(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  from_shelf_id: z.string(),
  from_shelf_location: z.string().nullable(),
  to_shelf_id: z.string(),
  to_shelf_location: z.string().nullable(),
  suggested_quantity: z.number(),
}).passthrough();

// preview entry (replenishment_orders_controller.rb:100-114)
//   - product_name / product_sku: `product&.` 安全導航 → nullable。
//   - inventory_type_id: need[:inventory_type_id]（slot 可能無 type → nullable）。
//   - shelf_location: `shelves[need[:shelf_id]]&.location` → nullable。
//   - need / over_capacity 來自 need hash；shortage 來自 plan entry，皆有值。
//   注意：preview entry **不含** inventory_type_name（只有 slot/order item serializer 才 emit）。
export const PreviewEntrySchema = z.object({
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  shelf_id: z.string(),
  shelf_location: z.string().nullable(),
  need: z.number(),
  over_capacity: z.boolean(),
  shortage: z.number(),
  items: z.array(PreviewItemSchema),
}).passthrough();

// preview split 摘要 (replenishment_orders_controller.rb #split_summary)
//   zone_id / zone_name 安全導航 → nullable；order_index / item_count / total_quantity 必有值。
export const PreviewSplitSummarySchema = z.object({
  order_index: z.number(),
  zone_id: z.string().nullable(),
  zone_name: z.string().nullable(),
  item_count: z.number(),
  total_quantity: z.number(),
}).passthrough();

// preview 回應 envelope 的 data（render_data_item({ entries, split })）。
export const PreviewResultSchema = z.object({
  entries: z.array(PreviewEntrySchema),
  split: z.array(PreviewSplitSummarySchema),
}).passthrough();

// `serialize_item` (replenishment_orders_controller.rb:132-141)
//   只在 show / generate (with_items: true) 出現於 order.items。
//   不同於 need/preview/slot serializer，serialize_item **全用直接存取（無 `&.`）**，
//   且其關聯 FK 在 replenishment_order_items 皆 NOT NULL（product_id / inventory_type_id /
//   from_shelf_id / to_shelf_id），對應名稱欄位也都 NOT NULL：
//     - product_name: `item.product.name`（products.name NOT NULL）→ non-nullable。
//     - inventory_type_id / inventory_type_name: FK NOT NULL + `item.inventory_type.name`
//       （inventory_types.name NOT NULL）→ 皆 non-nullable。
//     - from_shelf_location / to_shelf_location: `item.{from,to}_shelf.location`
//       （shelves.location NOT NULL）→ non-nullable。
//   故此 schema **不**比照 need/preview 放寬成 nullable——後端保證有值，放寬反而會遮蔽
//   資料完整性問題（feedback_no_safe_navigation_fallback）。
//     - product_sku: `item.product.sku`（products.sku nullable）→ 維持 nullable。
//     - suggested_quantity / moved_quantity: 整數欄位（DB NOT NULL，moved 預設 0）。
export const ReplenishmentOrderItemSchema = z.object({
  id: z.string(),
  status: z.string(),
  product_id: z.string(),
  product_name: z.string(),
  product_sku: z.string().nullable(),
  inventory_type_id: z.string(),
  inventory_type_name: z.string(),
  from_shelf_id: z.string(),
  from_shelf_location: z.string(),
  to_shelf_id: z.string(),
  to_shelf_location: z.string(),
  suggested_quantity: z.number(),
  moved_quantity: z.number(),
  // 預計搬移的庫存屬性（serialize_item 從 item.inventory 帶出）。
  //   - batch: inventories.batch nullable。
  //   - expiration_date / manufacturing_date: date 欄位，nullable，序列化為 ISO 字串。
  //   - custom_attributes: jsonb（DB default {}），key→value 物件。
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).default({}),
}).passthrough();

// `serialize_order` (replenishment_orders_controller.rb:118-130)
//   index / get_records / show / generate / cancel 共用。
//   - barcode: `order.barcode`（replenishment_orders.barcode citext NOT NULL）→ non-nullable。
//   - name: `order.name`（replenishment_orders.name citext NOT NULL）→ non-nullable。
//   - merchant_name: `order.merchant.name`（merchant 非 optional；merchants.name 欄位 nullable）→ nullable。
//   - completed_at: 欄位 nullable → nullable。
//   - items: 僅 with_items=true（show / generate）出現 → optional。
export const ReplenishmentOrderSchema = z.object({
  id: z.string(),
  barcode: z.string(),
  name: z.string(),
  status: z.string(),
  merchant_id: z.string(),
  merchant_name: z.string().nullable(),
  warehouse_id: z.string(),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  items: z.array(ReplenishmentOrderItemSchema).optional(),
}).passthrough();

// `destroy` (product_replenishment_shelves_controller.rb:42-44) → render_data_item({ id })
export const DestroyShelfResultSchema = z.object({
  id: z.string(),
}).passthrough();

// ============================================================
// Exported types
// ============================================================

export type ReplenishmentSlot = z.infer<typeof ReplenishmentSlotSchema>;
export type ReplenishmentNeed = z.infer<typeof ReplenishmentNeedSchema>;
export type PreviewItem = z.infer<typeof PreviewItemSchema>;
export type PreviewEntry = z.infer<typeof PreviewEntrySchema>;
export type PreviewSplitSummary = z.infer<typeof PreviewSplitSummarySchema>;
export type PreviewResult = z.infer<typeof PreviewResultSchema>;
export type ReplenishmentOrderItem = z.infer<typeof ReplenishmentOrderItemSchema>;
export type ReplenishmentOrder = z.infer<typeof ReplenishmentOrderSchema>;

// 揀補儲位唯一鍵 — (product, inventory_type, shelf) 三元組。
// 對齊後端 selected_slot_keys（replenishment_orders_controller.rb:62-64）。
// inventory_type_id 可能為 null（slot 未綁定庫存類型），故與後端 hash 一致允許 null。
export interface SlotKey {
  product_id: string;
  inventory_type_id: string | null;
  shelf_id: string;
}

// ============================================================
// Service
// ============================================================

const replenishmentService = {
  // --- 揀補儲位 slot 設定 (product_replenishment_shelves) ---

  // 表格用列表 (POST) — render_data_list
  getShelfRecords: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('product_replenishment_shelves/get_records', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(ReplenishmentSlotSchema));
  },

  // 單一揀補儲位 slot 詳情 (GET) — render_data_item(serialize_slot)
  // 命名為 getShelf 避免與補貨單詳情 `get` 撞名（同一個 service 物件）。
  getShelf: async (id: string) => {
    const res = await operatorApiFetch(`product_replenishment_shelves/${id}`, {
      method: 'GET',
    });
    return parseApiResponse(res, apiSuccess(ReplenishmentSlotSchema));
  },

  // 新增揀補儲位 slot
  createShelf: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('product_replenishment_shelves', {
      method: 'POST',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(ReplenishmentSlotSchema));
  },

  // 更新揀補儲位 slot
  updateShelf: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`product_replenishment_shelves/${id}`, {
      method: 'PUT',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(ReplenishmentSlotSchema));
  },

  // 刪除揀補儲位 slot
  destroyShelf: async (id: string) => {
    const res = await operatorApiFetch(`product_replenishment_shelves/${id}`, {
      method: 'DELETE',
    });
    return parseApiResponse(res, apiSuccess(DestroyShelfResultSchema));
  },

  // --- 補貨需求 (replenishment_needs) ---

  // 需求清單 (GET) — render_array_list
  getNeeds: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('replenishment_needs', {
      method: 'GET',
      params,
    });
    return parseApiResponse(res, apiList(ReplenishmentNeedSchema));
  },

  // --- 補貨單 (replenishment_orders) ---

  // 預覽補貨計畫 (POST) — render_data_item({ entries, split })（純計算，不寫 DB）
  previewOrder: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('replenishment_orders/preview', {
      method: 'POST',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(PreviewResultSchema));
  },

  // 產生補貨單 (POST) — render_array_list（拆分後 1..N 張，with_items）
  generateOrder: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('replenishment_orders/generate', {
      method: 'POST',
      body: data,
    });
    return parseApiResponse(res, apiList(ReplenishmentOrderSchema));
  },

  // 補貨單表格列表 (POST) — render_data_list
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('replenishment_orders/get_records', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(ReplenishmentOrderSchema));
  },

  // 單一補貨單詳情 (GET) — render_data_item(with_items: true)
  get: async (id: string) => {
    const res = await operatorApiFetch(`replenishment_orders/${id}`, {
      method: 'GET',
    });
    return parseApiResponse(res, apiSuccess(ReplenishmentOrderSchema));
  },

  // 取消補貨單 (POST) — 僅 pending（零搬移）可取消
  cancel: async (id: string) => {
    const res = await operatorApiFetch(`replenishment_orders/${id}/cancel`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(ReplenishmentOrderSchema));
  },

  // 提前結束補貨單 (POST) — 僅 in_progress 可結束（保留已搬、剩餘明細標 skipped）
  close: async (id: string) => {
    const res = await operatorApiFetch(`replenishment_orders/${id}/close`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(ReplenishmentOrderSchema));
  },
};

export { replenishmentService };
export default replenishmentService;
